import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { ApiError, getCurrentUser, type AuthSession } from "@/pages/auth/api";
import {
  AUTH_CHANGED_EVENT,
  clearSession,
  getStoredSessionToken,
  getStoredUsername,
} from "@/pages/auth/storage";

type AuthStatus = "anonymous" | "checking" | "authenticated";

type AuthSessionState = {
  status: AuthStatus;
  username: string | null;
};

const AuthSessionContext = createContext<AuthSessionState | null>(null);
const authRequestCache = new Map<string, Promise<AuthSession["user"]>>();

function readStoredState(): AuthSessionState {
  const sessionToken = getStoredSessionToken();
  const username = getStoredUsername();

  if (!sessionToken) {
    return {
      status: "anonymous",
      username: null,
    };
  }

  return {
    status: "checking",
    username,
  };
}

function getCurrentUserOnce(sessionToken: string) {
  const cached = authRequestCache.get(sessionToken);
  if (cached) {
    return cached;
  }

  const request = getCurrentUser(sessionToken).finally(() => {
    authRequestCache.delete(sessionToken);
  });
  authRequestCache.set(sessionToken, request);
  return request;
}

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthSessionState>(() => readStoredState());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const refresh = () => {
      setState(readStoredState());
      setRefreshKey((current) => current + 1);
    };

    window.addEventListener(AUTH_CHANGED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    const sessionToken = getStoredSessionToken();
    if (!sessionToken) {
      setState({
        status: "anonymous",
        username: null,
      });
      return;
    }

    let cancelled = false;
    const cachedUsername = getStoredUsername();
    setState({
      status: "checking",
      username: cachedUsername,
    });

    void getCurrentUserOnce(sessionToken)
      .then((user) => {
        if (cancelled) {
          return;
        }
        setState({
          status: "authenticated",
          username: user.username,
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        if (error instanceof ApiError && error.status === 401) {
          clearSession();
          setState({
            status: "anonymous",
            username: null,
          });
          return;
        }

        setState({
          status: cachedUsername ? "authenticated" : "anonymous",
          username: cachedUsername,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return createElement(AuthSessionContext.Provider, { value: state }, children);
}

export function useAuthSession() {
  const state = useContext(AuthSessionContext);
  if (state) {
    return state;
  }

  return readStoredState();
}
